import { effectiveLegendText, type Circuit } from "../circuit/circuit.js";
import type { Device } from "../device/device.js";
import type { Panel } from "../panel/panel.js";
import type { Room } from "../room/room.js";
import type { Site } from "../site/site.js";
import type { SiteGraph } from "../site/site-graph.js";
import {
  normalizeTag,
  type IndexedEntry,
  type SearchIndex,
} from "./index-shape.js";
import { indexField, type IndexedField } from "./ranking.js";

/**
 * Per-field weights for the Phase 5b ranker. Tuned so the *user-facing
 * primary identifier* of each entity (name on a device / room / site,
 * canonical short description on a circuit) outranks supporting
 * fields like notes or address lines.
 *
 * Pulled out as a frozen constant so a future calibration pass —
 * likely after Phase 8 AI room-scan fixtures land — can adjust
 * weights in one place without re-grepping the indexer.
 *
 * The legend-text-override (R-LABEL-11) carries the same weight as
 * the canonical description: the override is what *prints*, the
 * canonical is what's authoritative for reports — but a user
 * searching the index might know either, and both should rank a
 * circuit as a top-class match.
 */
export const SEARCH_FIELD_WEIGHTS = Object.freeze({
  // Sites
  siteName: 1.0,
  siteStreet: 0.5,
  siteCity: 0.5,
  siteRegion: 0.4,
  sitePostalCode: 0.4,
  // Rooms
  roomName: 1.0,
  roomFloor: 0.4,
  roomNotes: 0.4,
  // Panels
  panelId: 0.7,
  panelManufacturer: 0.6,
  panelModel: 0.7,
  // Circuits
  circuitShortDescription: 1.0,
  circuitLegendOverride: 1.0,
  circuitNotes: 0.5,
  circuitTag: 0.9,
  // Devices
  deviceName: 1.0,
  deviceNotes: 0.5,
  deviceLocationWithinRoom: 0.6,
  deviceNameplateManufacturer: 0.6,
  deviceNameplateModel: 0.7,
});

/**
 * Input shape per source site. The PWA generally indexes one site at
 * a time (the active one) but the type permits multiple — useful for a
 * pro-tier "search across all my sites" surface, and required so the
 * tests can prove cross-site isolation works correctly.
 *
 * `rooms` is optional: the `SiteGraph` does not carry rooms (rooms
 * are surfaced via `device.roomId` only), so the caller is responsible
 * for handing the room list in if they want rooms indexed. The Phase
 * 13 client will keep rooms alongside the graph; tests pass them
 * explicitly.
 */
export type IndexSource = {
  readonly graph: SiteGraph;
  readonly rooms?: ReadonlyArray<Room>;
};

/**
 * Build a `SearchIndex` from one or more sources. Pure constructor;
 * called whenever the underlying data changes drastically (initial
 * load, site switch). For granular edits (rename one device, drop a
 * tag), use the Phase 5c `updateIndex*` primitives — those return a
 * new index without re-projecting unaffected entries.
 *
 * Order of entries in the resulting index is deterministic but not
 * meaningful — the `search` function sorts hits by score, and the
 * Phase 5c filter sorts by tag membership. The build order is
 * site-by-site, then (per site) site → panels → circuits → devices
 * → rooms.
 */
export function buildIndex(sources: ReadonlyArray<IndexSource>): SearchIndex {
  const entries: IndexedEntry[] = [];
  const siteIds: string[] = [];

  for (const src of sources) {
    siteIds.push(src.graph.site.id);

    entries.push(buildSiteEntry(src.graph.site));

    for (const panel of src.graph.panels) {
      entries.push(buildPanelEntry(panel));
      for (const inst of panel.installations) {
        for (const c of inst.circuits) {
          entries.push(buildCircuitEntry(c));
        }
      }
    }

    for (const dev of src.graph.devices) {
      entries.push(buildDeviceEntry(dev));
    }

    for (const r of src.rooms ?? []) {
      entries.push(buildRoomEntry(r));
    }
  }

  return { entries, siteIds };
}

// -- Per-entity entry builders.
//
// Each builder is also exported so the Phase 5c update primitives can
// rebuild one entry in isolation, with the same field-weight rules
// applied. Keeping the builders public ensures the index can never go
// out-of-sync with itself: there is exactly one place per entity kind
// where the field set and weights are wired.

export function buildSiteEntry(s: Site): IndexedEntry {
  const fields: IndexedField[] = [
    indexField("name", SEARCH_FIELD_WEIGHTS.siteName, s.name),
    indexField("address.street", SEARCH_FIELD_WEIGHTS.siteStreet, s.address.street),
    indexField("address.city", SEARCH_FIELD_WEIGHTS.siteCity, s.address.city),
    indexField("address.region", SEARCH_FIELD_WEIGHTS.siteRegion, s.address.region),
    indexField(
      "address.postalCode",
      SEARCH_FIELD_WEIGHTS.sitePostalCode,
      s.address.postalCode,
    ),
  ];
  return { kind: "site", entity: s, entityId: s.id, fields, tags: [] };
}

export function buildPanelEntry(p: Panel): IndexedEntry {
  const fields: IndexedField[] = [
    indexField("id", SEARCH_FIELD_WEIGHTS.panelId, p.id),
    indexField(
      "manufacturer",
      SEARCH_FIELD_WEIGHTS.panelManufacturer,
      p.manufacturer ?? "",
    ),
    indexField("model", SEARCH_FIELD_WEIGHTS.panelModel, p.model ?? ""),
  ];
  return { kind: "panel", entity: p, entityId: p.id, fields, tags: [] };
}

export function buildRoomEntry(r: Room): IndexedEntry {
  const fields: IndexedField[] = [
    indexField("name", SEARCH_FIELD_WEIGHTS.roomName, r.name),
    indexField("floor", SEARCH_FIELD_WEIGHTS.roomFloor, r.floor ?? ""),
    indexField("notes", SEARCH_FIELD_WEIGHTS.roomNotes, r.notes ?? ""),
  ];
  return { kind: "room", entity: r, entityId: r.id, fields, tags: [] };
}

export function buildCircuitEntry(c: Circuit): IndexedEntry {
  const fields: IndexedField[] = [
    indexField(
      "shortDescription",
      SEARCH_FIELD_WEIGHTS.circuitShortDescription,
      c.shortDescription,
    ),
    indexField(
      "legendTextOverride",
      SEARCH_FIELD_WEIGHTS.circuitLegendOverride,
      c.legendTextOverride ?? "",
    ),
    // The effective text (override-when-present, canonical-otherwise)
    // is *also* surfaced as a field, with the same weight, so a query
    // exactly equal to the printed text always ranks top-class
    // regardless of which side stored it. This is redundant with one
    // of the two preceding fields by construction, but keeping it
    // explicit means a future override that pulls from elsewhere
    // (R-LABEL-11 variants) plugs in cleanly.
    indexField(
      "effectiveText",
      SEARCH_FIELD_WEIGHTS.circuitLegendOverride,
      effectiveLegendText(c),
    ),
    indexField("notes", SEARCH_FIELD_WEIGHTS.circuitNotes, c.notes ?? ""),
  ];
  for (const t of c.tags) {
    fields.push(indexField("tag", SEARCH_FIELD_WEIGHTS.circuitTag, t));
  }
  const tags = c.tags.map(normalizeTag);
  return { kind: "circuit", entity: c, entityId: c.id, fields, tags };
}

export function buildDeviceEntry(d: Device): IndexedEntry {
  const fields: IndexedField[] = [
    indexField("name", SEARCH_FIELD_WEIGHTS.deviceName, d.name),
    indexField("notes", SEARCH_FIELD_WEIGHTS.deviceNotes, d.notes ?? ""),
    indexField(
      "locationWithinRoom",
      SEARCH_FIELD_WEIGHTS.deviceLocationWithinRoom,
      d.locationWithinRoom ?? "",
    ),
    indexField(
      "nameplate.manufacturer",
      SEARCH_FIELD_WEIGHTS.deviceNameplateManufacturer,
      d.nameplate?.manufacturer ?? "",
    ),
    indexField(
      "nameplate.model",
      SEARCH_FIELD_WEIGHTS.deviceNameplateModel,
      d.nameplate?.model ?? "",
    ),
  ];
  return { kind: "device", entity: d, entityId: d.id, fields, tags: [] };
}
